/**
 * COM-05: Reportes de Compras — Dashboard y AP-Aging
 */
import { DataTable } from '@/components/ds/DataTable'
import { BarChart3, TrendingDown, Clock, AlertTriangle, Building2 } from 'lucide-react'
import { usePurchasesDashboard } from '../hooks/usePurchases'
import { EmptyState } from '@/components/EmptyState'
import { AGING_BUCKETS } from '@/components/ds/agingBuckets'

function fmtCurrency(value: string | number, currency: string) {
  const n = typeof value === 'string' ? parseFloat(value) : value
  return n.toLocaleString('es-CL', {
    style: 'currency',
    currency: currency || 'CLP',
    maximumFractionDigits: 0,
  })
}

export function PurchasesReportPage() {
  const { data, isLoading } = usePurchasesDashboard()

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-muted rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-surface-muted rounded-xl" />)}
          </div>
          <div className="h-64 bg-surface-muted rounded-xl" />
        </div>
      </div>
    )
  }

  const currency = data?.currency || 'CLP'
  const kpis = data?.kpis
  const aging = data?.ap_aging
  const topSuppliers = data?.top_suppliers || []

  // Catálogo único: acá +90 salía en rosa, más suave que 61-90.
  const agingBuckets = AGING_BUCKETS

  const totalAging = agingBuckets.reduce((sum, b) => {
    const val = aging?.[b.key]?.total
    return sum + (val ? parseFloat(String(val)) : 0)
  }, 0)

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-content">Reportes de Compras</h1>
        <p className="text-sm text-content-muted mt-0.5">
          Resumen de cuentas por pagar, vencimientos y proveedores principales
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
              <BarChart3 size={18} className="text-primary" />
            </div>
            <p className="text-sm text-content-muted font-medium">Facturado este mes</p>
          </div>
          <p className="text-2xl font-bold text-content">
            {kpis ? fmtCurrency(kpis.invoiced_this_month, currency) : '—'}
          </p>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-warning-surface flex items-center justify-center">
              <Clock size={18} className="text-warning-content-soft" />
            </div>
            <p className="text-sm text-content-muted font-medium">Por pagar</p>
          </div>
          <p className="text-2xl font-bold text-content">
            {kpis ? fmtCurrency(kpis.pending_payable, currency) : '—'}
          </p>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-success-surface flex items-center justify-center">
              <TrendingDown size={18} className="text-success-content-soft" />
            </div>
            <p className="text-sm text-content-muted font-medium">Pagado este mes</p>
          </div>
          <p className="text-2xl font-bold text-content">
            {kpis ? fmtCurrency(kpis.paid_this_month, currency) : '—'}
          </p>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-5 shadow-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-danger-surface flex items-center justify-center">
              <AlertTriangle size={18} className="text-danger-content-soft" />
            </div>
            <p className="text-sm text-content-muted font-medium">Facturas vencidas</p>
          </div>
          <p className="text-2xl font-bold text-danger-content-soft">
            {kpis?.overdue_count ?? '—'}
          </p>
        </div>
      </div>

      {/* AP-Aging */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <h2 className="text-base font-semibold text-content mb-4">Antigüedad de Saldos (AP-Aging)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          {agingBuckets.map((bucket) => {
            const bucketData = aging?.[bucket.key]
            const total = bucketData ? parseFloat(String(bucketData.total)) : 0
            const pct = totalAging > 0 ? (total / totalAging) * 100 : 0
            return (
              <div key={bucket.key} className={`rounded-xl border p-4 ${bucket.card}`}>
                <p className={`text-xs font-medium mb-1 ${bucket.text}`}>{bucket.label}</p>
                <p className={`text-lg font-bold ${bucket.text}`}>
                  {bucketData ? fmtCurrency(total, currency) : '—'}
                </p>
                <p className="text-xs text-content-muted mt-1">{bucketData?.count ?? 0} facturas</p>
                <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${bucket.bar} rounded-full transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Top Proveedores */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <h2 className="text-base font-semibold text-content mb-4">Top 5 Proveedores</h2>
        {topSuppliers.length === 0 ? (
          <EmptyState title="No hay datos de proveedores aún" size="sm" />
        ) : (
          <DataTable
            columns={[
              {
                key: 'puesto', header: '#', width: 48,
                render: (_s, i) => <span className="text-content-subtle font-medium">{i + 1}</span>,
              },
              {
                key: 'proveedor', header: 'Proveedor', sortable: true,
                sortValue: s => s.supplier_name,
                render: s => (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-surface-muted flex items-center justify-center shrink-0">
                      <Building2 size={13} className="text-content-muted" />
                    </div>
                    <span className="font-medium text-content">{s.supplier_name}</span>
                  </div>
                ),
              },
              {
                key: 'total', header: 'Total facturado', numeric: true, sortable: true,
                // Por el número, no por el texto con separadores de miles.
                sortValue: s => Number(s.total),
                render: s => <span className="font-semibold text-content">{fmtCurrency(s.total, currency)}</span>,
              },
            ]}
            rows={topSuppliers}
            rowKey={s => s.supplier_id}
            caption="Proveedores con mayor facturación"
          />
        )}
      </div>
    </div>
  )
}
